/**
 * AigntX design tokens.
 *
 * Brand tokens (primary / accent-1 / accent-2 / warn / danger) come from the
 * tenant profile and are written to the document root at runtime by
 * {@link applyTheme} — no rebuild. Everything else (neutrals, spacing, radius,
 * light/dark) is defined in `theme.css` and keyed off `data-theme`.
 */

export type ThemeMode = "light" | "dark";
export type ResolvedMode = "light" | "dark";
export type Direction = "ltr" | "rtl";
export type Locale = "en" | "ar";
export type RadiusScale = "sharp" | "soft" | "round";
export type FontFamily = "sans" | "grotesk";

/** The five colours a tenant controls. */
export interface TenantBrand {
  primary: string;
  accent1: string;
  accent2: string;
  warn: string;
  danger: string;
}

/** Full per-tenant appearance record (as stored in the tenant profile). */
export interface TenantTheme extends TenantBrand {
  name?: string;
  logo?: string | null;
  /** Brand glyph shown in the app mark when no logo image is set. */
  icon?: string;
  mode: ThemeMode;
  locale: Locale;
  dir: Direction;
  radius: RadiusScale;
  font: FontFamily;
}

/** House palette — NXGN-AI development identity (the four-colour X: cyan · sky ·
 * violet · fuchsia) used when a tenant hasn't customised. */
export const DEFAULT_BRAND: TenantBrand = {
  primary: "#22D3EE",
  accent1: "#8B5CF6",
  accent2: "#E879F9",
  warn: "#F5A524",
  danger: "#F5325B",
};

export const DEFAULT_THEME: TenantTheme = {
  ...DEFAULT_BRAND,
  name: "AigntX",
  logo: null,
  icon: "hexa",
  mode: "light",
  locale: "en",
  dir: "ltr",
  radius: "soft",
  font: "sans",
};

const RADIUS_PX: Record<RadiusScale, string> = {
  sharp: "4px",
  soft: "10px",
  round: "16px",
};

/** The RTL locales — used to auto-derive `dir` when it isn't set explicitly. */
export const RTL_LOCALES: ReadonlySet<Locale> = new Set<Locale>(["ar"]);

export function directionFor(locale: Locale): Direction {
  return RTL_LOCALES.has(locale) ? "rtl" : "ltr";
}

/** Brand colours → CSS custom-property map. */
export function brandVars(brand: TenantBrand): Record<string, string> {
  return {
    "--brand-primary": brand.primary,
    "--brand-accent-1": brand.accent1,
    "--brand-accent-2": brand.accent2,
    "--brand-warn": brand.warn,
    "--brand-danger": brand.danger,
  };
}

export function resolveMode(mode: ThemeMode): ResolvedMode {
  return mode;
}

/**
 * Apply a tenant theme to a DOM root (default: `document.documentElement`).
 * Writes brand vars + radius, and sets `data-theme`, `dir`, `lang`, `data-font`.
 * Safe to call on every change; it only touches attributes and inline vars.
 */
export function applyTheme(theme: TenantTheme, root?: HTMLElement): ResolvedMode {
  const el = root ?? (typeof document !== "undefined" ? document.documentElement : undefined);
  const resolved = resolveMode(theme.mode);
  if (!el) return resolved;

  const vars = brandVars(theme);
  vars["--radius-base"] = RADIUS_PX[theme.radius];
  for (const [k, v] of Object.entries(vars)) el.style.setProperty(k, v);

  el.setAttribute("data-theme", resolved);
  el.setAttribute("dir", theme.dir);
  el.setAttribute("lang", theme.locale);
  el.setAttribute("data-font", theme.font);
  return resolved;
}
